import { useUpdateOrganizationMutation } from "@/features/organizations/api/organizationsApi";
import { blankableUrl } from "@/features/organizations/lib/blankableUrl";
import { handleMutationError } from "@/shared/api/handleMutationError";
import { notify } from "@/shared/components/notify";
import { zodResolver } from "@/shared/forms/zodResolver";
import {
  type OrganizationResponse,
  UpdateOrganizationSchema,
} from "@keystone/contracts/organizations";
import { Button, Group, Modal, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface EditOrganizationModalProps {
  organization: OrganizationResponse;
  onClose: () => void;
}

interface FormValues {
  name: string;
  customerDomain: string;
  logoUrl: string;
}

export function EditOrganizationModal({ organization, onClose }: EditOrganizationModalProps) {
  const { t } = useTranslation("organizations");
  const [updateOrganization, { isLoading }] = useUpdateOrganizationMutation();

  const form = useForm<FormValues>({
    initialValues: {
      name: organization.name,
      customerDomain: organization.customerDomain ?? "",
      logoUrl: organization.logoUrl ?? "",
    },
    validate: zodResolver(
      UpdateOrganizationSchema.extend({
        customerDomain: z.string().trim().max(255).default(""),
        logoUrl: blankableUrl(t("form.errors.invalidUrl")).default(""),
      }),
    ),
    validateInputOnBlur: true,
  });

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      await updateOrganization({
        id: organization.id,
        body: {
          name: values.name,
          ...(values.customerDomain ? { customerDomain: values.customerDomain } : {}),
          ...(values.logoUrl ? { logoUrl: values.logoUrl } : {}),
        },
      }).unwrap();
      notify.success("organizations:notifications.updated");
      onClose();
    } catch (e) {
      handleMutationError(e, "organizations:errors.updateFailed");
    }
  });

  return (
    <Modal opened onClose={onClose} title={t("edit.title")} centered>
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap="sm">
          <TextInput
            label={t("form.name")}
            placeholder={t("form.namePlaceholder")}
            required
            {...form.getInputProps("name")}
          />
          <TextInput
            label={t("form.customerDomain")}
            placeholder={t("form.customerDomainPlaceholder")}
            {...form.getInputProps("customerDomain")}
          />
          <TextInput
            label={t("form.slug")}
            description={t("form.slugImmutable")}
            value={organization.slug}
            disabled
          />
          <TextInput
            label={t("form.logoUrl")}
            placeholder={t("form.logoUrlPlaceholder")}
            {...form.getInputProps("logoUrl")}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={onClose} type="button">
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" loading={isLoading} disabled={!form.isDirty()}>
              {t("edit.submit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
